import React from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {LogInterface} from "../../../../interfaces/private/LogInterface";
import LogStatusBadge from "../../LogV2/components/LogStatusBadge";
import {DashboardModuleKey} from "./DashboardModuleCard";

type ModuleMeta = {
    key: DashboardModuleKey;
    titleKey: string;
    logRoute: string;
};

type DashboardRecentActivityProps = {
    logs: LogInterface[];
    modules: ModuleMeta[];
    loading?: boolean;
    onView?: (id: string | number) => void;
};

const DashboardRecentActivity: React.FC<DashboardRecentActivityProps> = ({
    logs,
    modules,
    loading = false,
    onView,
}) => {
    const {t, i18n} = useTranslation();

    const moduleByType = modules.reduce<Record<string, ModuleMeta>>((acc, module) => {
        acc[module.key] = module;
        return acc;
    }, {});

    return (
        <div className="col-12 mb-3">
            <div className="business-detail bg-orange-bland-zp bdr-20 pd-25">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                    <div>
                        <h2 className="fw-bold fs-20 mb-1">{t("common.dashboard.recentActivityTitle")}</h2>
                        <p className="text-muted small mb-0">{t("common.dashboard.recentActivityHelp")}</p>
                    </div>
                </div>

                {loading ? (
                    <div className="placeholder-glow">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="placeholder col-12 mb-2 rounded" style={{height: 28}}/>
                        ))}
                    </div>
                ) : logs.length === 0 ? (
                    <p className="text-muted small mb-0">{t("common.dashboard.recentActivityEmpty")}</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                            <tr>
                                <th>{t("common.dashboard.recentActivityModule")}</th>
                                <th>{t("common.dashboard.recentActivityReference")}</th>
                                <th>{t("log.status")}</th>
                                <th>{t("log.message")}</th>
                                <th>{t("log.created_at")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {logs.map((log) => {
                                const moduleMeta = moduleByType[log.type];
                                const createdAt = moment(log.created_at);
                                const logRoute = moduleMeta?.logRoute ?? "#";
                                const openDetail = () => onView?.(log._id);

                                return (
                                    <tr
                                        key={String(log._id)}
                                        className={onView ? "cursor-pointer" : undefined}
                                        style={onView ? {cursor: "pointer"} : undefined}
                                        onClick={onView ? openDetail : undefined}
                                    >
                                        <td>
                                            {moduleMeta ? (
                                                <Link
                                                    to={logRoute}
                                                    className="fw-semibold text-decoration-none"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {t(moduleMeta.titleKey)}
                                                </Link>
                                            ) : (
                                                log.type
                                            )}
                                        </td>
                                        <td className="text-break">{log.ref_id || "-"}</td>
                                        <td>
                                            <LogStatusBadge status={log.status} matchingStatus={log.data1}/>
                                        </td>
                                        <td
                                            className="text-truncate"
                                            style={{maxWidth: "280px"}}
                                            title={log.message || ""}
                                        >
                                            {(log.message || "").substring(0, 80)}
                                        </td>
                                        <td className="text-nowrap">
                                            {createdAt.isValid()
                                                ? createdAt.locale(i18n.language).format("DD/MM/YYYY HH:mm")
                                                : "-"}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(DashboardRecentActivity);
